import React from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import {
  createRootRoute, createRoute, createRouter, RouterProvider,
} from '@tanstack/react-router';
import App, { useAppContext } from './App';
import LoginPage from './routes/Login';
import VerifyPage from './routes/AdminVerify';
import TriagePage from './routes/Triage';
import ApplicationsPage from './routes/Applications';
import ApplicationDetailPage from './routes/ApplicationDetail';
import FinancePage from './routes/Finance';
import AdminSourcesPage from './routes/AdminSources';
import AdminAuditPage from './routes/AdminAudit';
import SnapshotPage from './routes/Snapshot';
import GovernancePage from './routes/Governance';
import WelcomePage from './routes/Welcome';
import PartnersPage from './routes/Partners';
import ProjectsPage from './routes/Projects';
import ProjectNewPage from './routes/ProjectNew';
import ProjectDetailPage from './routes/ProjectDetail';
import './styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: 15_000, retry: 1 } },
});

const rootRoute = createRootRoute({ component: App });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: TriageWithCtx,
});

function TriageWithCtx() {
  const { showShortcuts, setShowShortcuts } = useAppContext();
  return <TriagePage showShortcuts={showShortcuts} setShowShortcuts={setShowShortcuts} />;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => <LoginPage redirectTo="/" />,
});

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/verify',
  component: VerifyPage,
});

const applicationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications',
  component: ApplicationsPage,
});

const applicationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/applications/$id',
  component: ApplicationDetailPage,
});

const financeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/finance',
  component: FinancePage,
});

const adminSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/sources',
  component: AdminSourcesPage,
});

const adminAuditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/audit',
  component: AdminAuditPage,
});

const snapshotRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/snapshot',
  component: SnapshotPage,
});

const governanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/governance',
  component: GovernancePage,
});

const welcomeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/welcome',
  component: WelcomePage,
});

const partnersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/partners',
  component: PartnersPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects',
  component: ProjectsPage,
});

const projectNewRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/new',
  component: ProjectNewPage,
});

const projectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$id',
  component: ProjectDetailPage,
});

const router = createRouter({
  routeTree: rootRoute.addChildren([
    indexRoute,
    loginRoute,
    verifyRoute,
    applicationsRoute,
    applicationDetailRoute,
    financeRoute,
    adminSourcesRoute,
    adminAuditRoute,
    snapshotRoute,
    governanceRoute,
    welcomeRoute,
    partnersRoute,
    projectsRoute,
    projectNewRoute,
    projectDetailRoute,
  ]),
});

declare module '@tanstack/react-router' {
  interface Register { router: typeof router }
}

const root = createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </React.StrictMode>,
);
